import { useRef, useState } from "react";
import { Upload, X, AlertTriangle } from "lucide-react";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * Single storefront brand-asset upload slot.
 * Validates file type, dimensions and size against the spec.
 *
 * spec fields (all optional, but type+dims should be set):
 *  - accept: comma-separated MIME (e.g. "image/png")
 *  - acceptLabel: human label ("PNG", "JPG")
 *  - exactWidth, exactHeight: required exact dimensions
 *  - minWidth, minHeight: required minimums
 *  - aspectMin, aspectMax: width/height ratio bounds (used when exact dims absent)
 *  - preferredKb: warn if larger
 *  - maxKb: hard block above this (defaults to uploader limit, 6144 KB)
 *  - recommendation: human-readable line shown under the slot
 */
const HARD_MAX_KB = 6 * 1024;

export default function StorefrontAssetSlot({ label, value, onChange, spec, testId }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [warn, setWarn] = useState("");

    const accept = spec.accept || "image/png,image/jpeg,image/webp";
    const maxKb = spec.maxKb || HARD_MAX_KB;
    const previewUrl = value ? imageUrl(value) : "";

    const readDims = (file) =>
        new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onerror = () => reject(new Error("Could not read file"));
            fr.onload = () => {
                const img = new Image();
                img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
                img.onerror = () => reject(new Error("Could not parse image"));
                img.src = fr.result;
            };
            fr.readAsDataURL(file);
        });

    const validate = async (file) => {
        if (!accept.split(",").map((s) => s.trim()).includes(file.type)) {
            return `Wrong file type. Allowed: ${spec.acceptLabel || accept}`;
        }
        const sizeKb = file.size / 1024;
        if (sizeKb > maxKb) {
            return `File too large (${Math.round(sizeKb)} KB). Hard max ${maxKb} KB.`;
        }
        const { width, height } = await readDims(file);
        if (spec.exactWidth && spec.exactHeight) {
            if (width !== spec.exactWidth || height !== spec.exactHeight) {
                return `Wrong dimensions: got ${width}×${height}, need exactly ${spec.exactWidth}×${spec.exactHeight} px.`;
            }
        } else {
            if (spec.minWidth && width < spec.minWidth) {
                return `Width too small: ${width}px (min ${spec.minWidth}px).`;
            }
            if (spec.minHeight && height < spec.minHeight) {
                return `Height too small: ${height}px (min ${spec.minHeight}px).`;
            }
            const ratio = width / height;
            if (spec.aspectMin && ratio < spec.aspectMin) {
                return `Aspect ratio too tall: ${ratio.toFixed(2)}:1 (min ${spec.aspectMin}:1).`;
            }
            if (spec.aspectMax && ratio > spec.aspectMax) {
                return `Aspect ratio too wide: ${ratio.toFixed(2)}:1 (max ${spec.aspectMax}:1).`;
            }
        }
        return null; // ok
    };

    const handleFile = async (file) => {
        if (!file) return;
        setWarn("");
        setBusy(true);
        try {
            const err = await validate(file);
            if (err) {
                toast.error(err);
                setBusy(false);
                return;
            }
            // soft-warn for preferred size
            const sizeKb = file.size / 1024;
            if (spec.preferredKb && sizeKb > spec.preferredKb) {
                setWarn(
                    `File is ${Math.round(sizeKb)} KB — recommended < ${spec.preferredKb} KB. Consider compressing.`
                );
            }
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success(`${label} uploaded`);
        } catch (e) {
            toast.error(`Upload failed: ${e?.message || ""}`);
        }
        setBusy(false);
    };

    return (
        <div
            className="border border-[#27272A] bg-[#0A0A0B] rounded-sm p-3 flex gap-3"
            data-testid={`asset-slot-${testId}`}
        >
            <div className="w-24 h-24 flex-shrink-0 border border-[#27272A] rounded-sm bg-[#121214] overflow-hidden flex items-center justify-center">
                {previewUrl ? (
                    <img
                        src={previewUrl}
                        alt={label}
                        className="w-full h-full object-contain"
                        data-testid={`asset-preview-${testId}`}
                    />
                ) : (
                    <span className="text-[10px] text-white/40 uppercase tracking-widest font-display">
                        No file
                    </span>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-display uppercase tracking-tight font-bold text-white text-sm">
                        {label}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 border border-[#27272A] hover:border-[#00BCE5] hover:text-[#00BCE5] text-white px-3 py-1.5 rounded-sm font-display uppercase tracking-widest text-[11px] disabled:opacity-50"
                            data-testid={`asset-upload-${testId}`}
                        >
                            <Upload size={12} /> {busy ? "Uploading…" : value ? "Replace" : "Upload"}
                        </button>
                        {value && (
                            <button
                                type="button"
                                onClick={() => onChange("")}
                                className="inline-flex items-center gap-1.5 border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF6E66] text-white/60 px-2 py-1.5 rounded-sm font-display uppercase tracking-widest text-[11px]"
                                data-testid={`asset-clear-${testId}`}
                            >
                                <X size={12} /> Clear
                            </button>
                        )}
                    </div>
                </div>
                <div className="text-[11px] text-white/55 mt-1 leading-relaxed">
                    {spec.recommendation}
                </div>
                {warn && (
                    <div
                        className="mt-2 text-[11px] text-[#FF9F0A] flex items-center gap-1.5"
                        data-testid={`asset-warn-${testId}`}
                    >
                        <AlertTriangle size={12} /> {warn}
                    </div>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                />
            </div>
        </div>
    );
}
